/**
 * One timer + in-flight fetch per key so Topbar, Dashboard, and listing
 * badges do not each hit Sharetribe on their own 20s loops.
 */

const channels = new Map();

const isDocumentHidden = () =>
  typeof document !== 'undefined' && document.visibilityState === 'hidden';

const clearTimer = channel => {
  if (channel.timer != null && typeof channel.clearTimer === 'function') {
    channel.clearTimer(channel.timer);
  }
  channel.timer = null;
};

/**
 * Drop every shared poll. Tests only.
 */
export const resetSharedPollsForTests = () => {
  channels.forEach(channel => {
    clearTimer(channel);
  });
  channels.clear();
};

/**
 * Run the next tick now (refresh events / tests).
 *
 * @param {string} key
 */
export const pokeSharedPoll = key => {
  const channel = channels.get(key);
  if (channel?.tick) {
    channel.tick({ force: true });
  }
};

const channelListenerCount = key => channels.get(key)?.listeners?.size || 0;

/**
 * Subscribe to a shared poll. The first subscriber starts the loop.
 * Later subscribers reuse the last payload and the same in-flight fetch.
 *
 * @param {Object} params
 * @param {string} params.key
 * @param {number} params.intervalMs
 * @param {Function} params.fetchFn
 * @param {Function} params.onData  (error, data) => void
 * @param {Function} [params.shouldBackoff]
 * @param {Function} [params.nextDelayMs]
 * @param {boolean} [params.skipWhenHidden]
 * @returns {Function} unsubscribe
 */
export const subscribeSharedPoll = ({
  key,
  intervalMs,
  fetchFn,
  onData,
  shouldBackoff,
  nextDelayMs,
  skipWhenHidden = true,
  hiddenIntervalMs = 0,
}) => {
  if (!key || typeof fetchFn !== 'function' || typeof onData !== 'function') {
    return () => {};
  }

  let channel = channels.get(key);
  if (!channel) {
    // Bound to the global: browsers throw "Illegal invocation" when setTimeout is
    // called as a method of another object (channel.scheduleTimer(...)).
    const schedule = typeof setTimeout === 'function' ? setTimeout.bind(globalThis) : null;
    const clear = typeof clearTimeout === 'function' ? clearTimeout.bind(globalThis) : null;
    channel = {
      key,
      intervalMs,
      fetchFn,
      shouldBackoff,
      nextDelayMs,
      skipWhenHidden,
      hiddenIntervalMs,
      lastFetchAt: 0,
      listeners: new Set(),
      lastData: undefined,
      delay: intervalMs,
      timer: null,
      inFlight: false,
      scheduleTimer: schedule,
      clearTimer: clear,
    };

    const emit = (error, data) => {
      channel.listeners.forEach(listener => {
        listener(error, data);
      });
    };

    const scheduleNext = delay => {
      clearTimer(channel);
      if (!channel.scheduleTimer || channel.listeners.size === 0) {
        return;
      }
      channel.timer = channel.scheduleTimer(() => channel.tick(), delay);
    };

    channel.tick = ({ force = false } = {}) => {
      if (!force && channel.listeners.size === 0) {
        return;
      }
      if (channel.inFlight) {
        return;
      }
      if (!force && isDocumentHidden()) {
        // Backgrounded tab: either skip, or keep polling at a slower pace so
        // other-tab users still get browser alerts while the site is open.
        const slow = channel.hiddenIntervalMs > 0;
        if (
          (channel.skipWhenHidden && !slow) ||
          (slow && Date.now() - channel.lastFetchAt < channel.hiddenIntervalMs)
        ) {
          scheduleNext(channel.intervalMs);
          return;
        }
      }
      channel.inFlight = true;
      channel.lastFetchAt = Date.now();
      Promise.resolve()
        .then(() => channel.fetchFn())
        .then(data => {
          channel.lastData = data;
          channel.delay = channel.intervalMs;
          emit(null, data);
        })
        .catch(error => {
          if (typeof channel.shouldBackoff === 'function' && channel.shouldBackoff(error)) {
            channel.delay =
              typeof channel.nextDelayMs === 'function'
                ? channel.nextDelayMs(error, channel.delay)
                : channel.delay;
          }
          emit(error, channel.lastData);
        })
        .finally(() => {
          channel.inFlight = false;
          if (channel.listeners.size > 0) {
            scheduleNext(channel.delay);
          }
        });
    };

    channels.set(key, channel);
  }

  const startedEmpty = channel.listeners.size === 0;
  channel.listeners.add(onData);
  if (channel.lastData !== undefined) {
    onData(null, channel.lastData);
  }
  if (startedEmpty) {
    channel.tick();
  }

  return () => {
    channel.listeners.delete(onData);
    if (channel.listeners.size === 0) {
      clearTimer(channel);
      channels.delete(key);
    }
  };
};

export const sharedPollListenerCountForTests = channelListenerCount;
